<template>
	<div class="cards-widget-field">
		<default-widget-field v-bind="defaultFieldProps" v-on="defaultFieldListeners" />
		<v-input :ref="fieldRef" data-lpignore="true" v-model="value" v-bind="activeFormFieldCommonProps">
			<div v-if="!!template" class="cards-container">
				<v-text-field
					v-if="isSearch"
					dense
					outlined
					data-lpignore="true"
					hide-details="auto"
					class="search-field"
					prepend-inner-icon="mdi-magnify"
					@input="(str) => (searchText = str)"
				/>
				<div class="cards-list-container">
					<div v-for="(row, index) in formattedPreviousSteps" :key="index" @click="toggleSelection(row)" class="card-display-container">
						<CardDisplay :cardData="row" :cardTemplate="template" :isSelected="isSelected(row)" :isSelectionOn="userSelectionEnabled" />
					</div>
				</div>
				<div class="bottom-bar flex-space-between--center">
					<div class="left-side" :class="{ 'opacity-0': !userSelectionEnabled }">{{ amountSelected }} Selected</div>
					<div class="right-side">
						<span> {{ formattedPreviousSteps.length }} Cards</span>
					</div>
				</div>
			</div>
			<div v-else class="cards-container">
				<div class="empty-cards-field flex-centerize">Please select template</div>
			</div>
		</v-input>
	</div>
</template>

<script>
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';

import { mapGetters } from 'vuex';

import MultiItemSourceFieldMixin from '../MultiItemSourceField/MultiItemSourceFieldMixin';
import WidgetFieldMixin from '../WidgetFieldMixin';

import { interpolateString } from '@/utils/helpers';

import DefaultWidgetField from '../DefaultWidgetField';
import CardDisplay from './CardDisplay';

function getPlacementFromParamsMap(step, paramsMap) {
	const fields = {};
	for (const [paramKey, param] of Object.entries(paramsMap)) {
		fields[paramKey] = {
			...param,
			index: paramKey,
			value: interpolateString(param.value || '', step), // the param should have iniizialized, in case iots not, for backwards reason, we'll leave it for now, and remove it later
		};
	}
	return fields;
}

export default {
	name: 'CardsField',
	components: {
		CardDisplay,
		DefaultWidgetField,
	},
	mixins: [WidgetFieldMixin, MultiItemSourceFieldMixin],
	computed: {
		...mapGetters('cardsSettings', ['getCurrentEditedPlacementForCard']),
		currentEditedPlacement() {
			return this.getCurrentEditedPlacementForCard(this.field.id);
		},
		cardsSettingsData() {
			if (!this.field.metadata.params_map || !this.previousStepsEditMode) {
				return { fields: [] };
			}
			const firstRow = this.previousStepsEditMode[0];
			return { fields: getPlacementFromParamsMap(firstRow, this.field.metadata.params_map), step: firstRow };
		},
		template() {
			return this.field.metadata.options.template ? this.field.metadata.options.template.value : null;
		},
		formattedPreviousSteps() {
			const allFields = this.preStep.map((step) => ({ fields: getPlacementFromParamsMap(step, this.field.metadata.params_map), step }));
			if (this.isSearch) {
				return allFields.filter((f) => {
					const isIncluded = Object.values(f.fields).some((field) =>
						String(field.value).toLowerCase().includes(this.searchText.toLowerCase())
					);
					return isIncluded;
				});
			} else {
				return allFields;
			}
		},
	},
	methods: {
		toggleSelection(row) {
			if (!this.userSelectionEnabled) {
				return;
			}
			if (this.isMultipleSelection) {
				const currentStepIndex = this.selections.findIndex((step) => isEqual(step, row.step));
				if (currentStepIndex === -1) {
					// not selected yet
					this.selections.push(row.step);
				} else {
					// remove selection of step
					this.selections.splice(currentStepIndex, 1);
				}
				this.value = this.selections;
			} else {
				const selectedRow = this.selections.length > 0 ? this.selections[0] : null;
				if (isEqual(selectedRow, row)) {
					this.value = null;
					this.selections.pop();
				} else if (row) {
					const { step } = row;
					this.value = step;
					this.selections = [step];
				}
			}
			if (this.field.metadata.options.submit_on_select.enabled) {
				this.$emit('applyInterfaceSubmit');
			}
		},
		isSelected(row) {
			if (this.isMultipleSelection) {
				const found = this.selections.findIndex((step) => isEqual(step, row.step)) !== -1;
				return found;
			} else {
				const selectedRow = this.selections.length > 0 ? this.selections[0] : null;
				return selectedRow && isEqual(selectedRow, row.step);
			}
		},
		initializePreSelectedValue() {
			if (this.value) {
				if (isArray(this.value)) {
					this.selections = this.value;
				} else {
					const selectedRow = this.formattedPreviousSteps.find((step) => isEqual(step.step, this.value));
					if (selectedRow !== -1) {
						this.toggleSelection(selectedRow);
					}
				}
			}
		},
	},
	watch: {
		value: {
			handler(newV) {
				if (isArray(newV) && newV.length === 0) {
					// in case we re-enter this component and the value is empty, we clean the selections array
					this.selections = [];
				}
			},
			immediate: true,
			deep: true,
		},
	},
	mounted() {
		this.initializePreSelectedValue();
	},
	data() {
		return {
			selections: [], // to manage selected blocks values.
		};
	},
};
</script>

<style scoped lang="scss">
.cards-widget-field {
	$border-color: #e1e4ea;
	text-align: left;

	.cards-container {
		border: 1px solid $border-color;
		border-radius: 7px;
		overflow: hidden;

		.cards-list-container {
			padding: 10px;
			overflow-y: auto;
			max-height: 284px;
		}
	}

	.card-display-container {
		margin: 10px 0;
	}

	.search-field {
		padding: 10px;
		border-bottom: 1px solid $border-color;
		border-radius: 0;
		background-color: #fcfcfd;

		::v-deep {
			fieldset {
				border-radius: 6px;
				border: 1px solid #dfe4ef;
			}
			.v-input__control {
				background-color: #fff;
			}
			.v-input__slot {
				display: inherit !important; // hackish fix
			}
		}
	}

	.empty-cards-field {
		height: 100px;
		font-weight: 200;
	}
	.bottom-bar {
		padding: 4px 15px;
		font-size: 11px;
		font-weight: 200px;
		border-top: 1px solid $border-color;
		background-color: #fff;

		span {
			margin-left: 8px;
		}
	}

	:not(.search-field) {
		::v-deep {
			.v-input__slot {
				display: block; // hackish fix
			}
		}
	}

	@media (max-width: $device-sm) {
		.cards-container {
			background-color: var(--widget-field-mobile-bg) !important;
			border-radius: 6px;
		}
	}
}
</style>
